import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

const HIGHLIGHT_DURATION_MS = 4_000;

export const AI_FILLED_CLASS_NAME = 'ai-filled-flash';

interface AiFilledFieldsContextValue {
  readonly filled: ReadonlySet<string>;
  readonly markFilled: (names: readonly string[]) => void;
}

const noopContext: AiFilledFieldsContextValue = {
  filled: new Set(),
  markFilled: () => undefined,
};

const AiFilledFieldsContext = createContext<AiFilledFieldsContextValue>(noopContext);

const providerStyle: CSSProperties = {
  display: 'contents',
  ['--ai-filled-duration' as string]: `${String(HIGHLIGHT_DURATION_MS)}ms`,
};

export function AiFilledFieldsProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [filled, setFilled] = useState<ReadonlySet<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const markFilled = useCallback((names: readonly string[]): void => {
    if (names.length === 0) return;
    setFilled(new Set(names));
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setFilled(new Set());
      timerRef.current = null;
    }, HIGHLIGHT_DURATION_MS);
  }, []);

  const value = useMemo<AiFilledFieldsContextValue>(
    () => ({ filled, markFilled }),
    [filled, markFilled],
  );

  return (
    <AiFilledFieldsContext.Provider value={value}>
      <div style={providerStyle}>{children}</div>
    </AiFilledFieldsContext.Provider>
  );
}

export function useMarkAiFilledFields(): (names: readonly string[]) => void {
  return useContext(AiFilledFieldsContext).markFilled;
}

export function useIsAiFilledField(name: string): boolean {
  return useContext(AiFilledFieldsContext).filled.has(name);
}
